// Page Checkout - Design Licorne Premium
// Tunnel de paiement optimisé en 3 étapes

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, MapPin, Truck, CreditCard, Check, 
  Shield, Lock, Plus, Edit2, Package, Clock, ChevronRight,
  AlertTriangle, Home, Building, Sparkles, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import { useBuyer } from '../../contexts/BuyerContext';
import { API_URL } from '../../config/api';

// Étapes du checkout
const STEPS = [
  { id: 1, label: 'Livraison', icon: MapPin },
  { id: 2, label: 'Mode de livraison', icon: Truck },
  { id: 3, label: 'Paiement', icon: CreditCard }
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { 
    cart, 
    cartSubtotal, 
    shippingCost, 
    cartTotal, 
    cartCount,
    isAuthenticated,
    user,
    clearCart 
  } = useBuyer();

  const [currentStep, setCurrentStep] = useState(1);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('standard');
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderCreated, setOrderCreated] = useState(null);

  // Formulaire nouvelle adresse
  const [addressForm, setAddressForm] = useState({
    full_name: '',
    phone: '',
    email: '',
    address_line1: '',
    address_line2: '',
    postal_code: '',
    city: '',
    country: 'France',
    instructions: '',
    is_default: false
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/connexion-acheteur?redirect=/checkout');
      return;
    }
    if (cart.length === 0 && !orderCreated) {
      navigate('/panier');
      return;
    }
    loadAddresses();
  }, [isAuthenticated, cart.length]);

  const loadAddresses = async () => {
    try {
      const token = localStorage.getItem('buyer_token');
      const res = await fetch(`${API_URL}/api/me/addresses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAddresses(data);
        // Sélectionner l'adresse par défaut
        const defaultAddr = data.find(a => a.is_default);
        if (defaultAddr) setSelectedAddressId(defaultAddr.id);
        else if (data.length > 0) setSelectedAddressId(data[0].id);
      }
    } catch (e) {
      console.error('Error loading addresses:', e);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('buyer_token');
      const res = await fetch(`${API_URL}/api/me/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(addressForm)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Adresse ajoutée');
        setShowAddressForm(false);
        await loadAddresses();
        setSelectedAddressId(data.id);
        setAddressForm({
          full_name: '',
          phone: '',
          email: '',
          address_line1: '',
          address_line2: '',
          postal_code: '',
          city: '',
          country: 'France',
          instructions: '',
          is_default: false
        });
      } else {
        toast.error('Erreur lors de l\'ajout de l\'adresse');
      }
    } catch (e) {
      toast.error('Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!selectedAddressId) {
      toast.error('Sélectionne une adresse de livraison');
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('buyer_token');
      const res = await fetch(`${API_URL}/api/me/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          shipping_address_id: selectedAddressId,
          delivery_method: deliveryMethod,
          payment_method: 'card',
          customer_note: ''
        })
      });

      if (res.ok) {
        const data = await res.json();
        setOrderCreated(data);
        clearCart();
        toast.success('Commande créée avec succès !');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la création de la commande');
      }
    } catch (e) {
      toast.error('Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(price || 0);
  };

  const deliveryOptions = [
    { 
      id: 'standard', 
      label: 'Livraison Standard', 
      delay: '3-5 jours ouvrés',
      price: shippingCost,
      icon: Truck
    },
    { 
      id: 'express', 
      label: 'Livraison Express', 
      delay: '24-48h',
      price: 9.99,
      icon: Sparkles,
      highlight: true
    },
    { 
      id: 'pickup', 
      label: 'Retrait en point relais', 
      delay: '3-5 jours ouvrés',
      price: 0,
      icon: Building
    }
  ];

  const selectedAddress = addresses.find(a => a.id === selectedAddressId);
  const selectedDelivery = deliveryOptions.find(d => d.id === deliveryMethod);
  const finalShipping = selectedDelivery?.price || shippingCost;
  const finalTotal = cartSubtotal + finalShipping;

  // Page de confirmation
  if (orderCreated) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <UnifiedHeader />
        <MainNavigation />
        <div className="h-24 md:hidden" />

        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.6 }}
            className="w-24 h-24 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-6"
          >
            <CheckCircle size={48} className="text-emerald-500" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            <h1 className="text-2xl font-bold text-gray-900">Merci pour ta commande !</h1>
            <p className="text-gray-600">
              Ta commande <span className="font-semibold text-[#F97316]">#{orderCreated.id?.slice(0, 8).toUpperCase()}</span> a bien été enregistrée.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left mt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Mode Préouverture</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Cette commande est une simulation. Les paiements réels seront activés prochainement.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-6">
              <Link to="/mes-commandes">
                <Button variant="outline" className="w-full sm:w-auto">
                  <Package size={18} className="mr-2" />
                  Voir ma commande
                </Button>
              </Link>
              <Link to="/produits">
                <Button className="w-full sm:w-auto bg-[#F97316] hover:bg-[#EA580C]">
                  Continuer mes achats
                </Button>
              </Link>
            </div>
          </motion.div>
        </main>

        
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <UnifiedHeader />
      <MainNavigation />
      <div className="h-24 md:hidden" />

      {/* Header avec étapes */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate('/panier')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Finaliser ma commande</h1>
          </div>

          {/* Stepper */}
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex items-center gap-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    currentStep > step.id 
                      ? 'bg-emerald-500 text-white' 
                      : currentStep === step.id 
                        ? 'bg-[#F97316] text-white' 
                        : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step.id ? <Check size={18} /> : <step.icon size={18} />}
                  </div>
                  <span className={`hidden sm:block text-sm font-medium ${
                    currentStep >= step.id ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {step.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-emerald-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          
          {/* Colonne principale */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              
              {/* ÉTAPE 1: Adresse de livraison */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="bg-white rounded-2xl shadow-sm p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Adresse de livraison</h2>

                    {addresses.length === 0 && !showAddressForm ? (
                      <div className="text-center py-8">
                        <MapPin size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500 mb-4">Aucune adresse enregistrée</p>
                        <Button onClick={() => setShowAddressForm(true)} className="bg-[#F97316] hover:bg-[#EA580C]">
                          <Plus size={18} className="mr-2" />
                          Ajouter une adresse
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {addresses.map((addr) => (
                          <label
                            key={addr.id}
                            className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              selectedAddressId === addr.id 
                                ? 'border-[#F97316] bg-orange-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="radio"
                                name="address"
                                checked={selectedAddressId === addr.id}
                                onChange={() => setSelectedAddressId(addr.id)}
                                className="mt-1 accent-[#F97316]"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900">{addr.full_name || 'Adresse'}</span>
                                  {addr.is_default && (
                                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Par défaut</span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500 mt-1">{addr.address_line1}</p>
                                {addr.address_line2 && <p className="text-sm text-gray-500">{addr.address_line2}</p>}
                                <p className="text-sm text-gray-500">{addr.postal_code} {addr.city}</p>
                                <p className="text-sm text-gray-500">{addr.phone}</p>
                              </div>
                            </div>
                          </label>
                        ))}

                        {!showAddressForm && (
                          <button
                            onClick={() => setShowAddressForm(true)}
                            className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-[#F97316] hover:text-[#F97316] flex items-center justify-center gap-2 transition-colors"
                          >
                            <Plus size={18} />
                            Ajouter une nouvelle adresse
                          </button>
                        )}
                      </div>
                    )}

                    {/* Formulaire nouvelle adresse */}
                    <AnimatePresence>
                      {showAddressForm && (
                        <motion.form
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          onSubmit={handleAddAddress}
                          className="mt-4 pt-4 border-t border-gray-100 space-y-4"
                        >
                          <h3 className="font-semibold text-gray-900">Nouvelle adresse</h3>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <Input
                              placeholder="Nom complet"
                              value={addressForm.full_name}
                              onChange={(e) => setAddressForm({...addressForm, full_name: e.target.value})}
                              required
                            />
                            <Input
                              placeholder="Téléphone"
                              value={addressForm.phone}
                              onChange={(e) => setAddressForm({...addressForm, phone: e.target.value})}
                              required
                            />
                          </div>
                          
                          <Input
                            placeholder="Email (optionnel)"
                            type="email"
                            value={addressForm.email}
                            onChange={(e) => setAddressForm({...addressForm, email: e.target.value})}
                          />
                          
                          <Input
                            placeholder="Adresse (numéro et rue)"
                            value={addressForm.address_line1}
                            onChange={(e) => setAddressForm({...addressForm, address_line1: e.target.value})}
                            required
                          />
                          
                          <Input
                            placeholder="Complément (appartement, étage...)"
                            value={addressForm.address_line2}
                            onChange={(e) => setAddressForm({...addressForm, address_line2: e.target.value})}
                          />
                          
                          <div className="grid grid-cols-3 gap-4">
                            <Input
                              placeholder="Code postal"
                              value={addressForm.postal_code}
                              onChange={(e) => setAddressForm({...addressForm, postal_code: e.target.value})}
                              required
                            />
                            <Input
                              placeholder="Ville"
                              value={addressForm.city}
                              onChange={(e) => setAddressForm({...addressForm, city: e.target.value})}
                              required
                              className="col-span-2"
                            />
                          </div>
                          
                          <Input
                            placeholder="Instructions de livraison (optionnel)"
                            value={addressForm.instructions}
                            onChange={(e) => setAddressForm({...addressForm, instructions: e.target.value})}
                          />

                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={addressForm.is_default}
                              onChange={(e) => setAddressForm({...addressForm, is_default: e.target.checked})}
                              className="accent-[#F97316]"
                            />
                            Définir comme adresse par défaut
                          </label>

                          <div className="flex gap-3">
                            <Button type="button" variant="outline" onClick={() => setShowAddressForm(false)}>
                              Annuler
                            </Button>
                            <Button type="submit" className="bg-[#F97316] hover:bg-[#EA580C]" disabled={loading}>
                              {loading ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                          </div>
                        </motion.form>
                      )}
                    </AnimatePresence>
                  </div>

                  <Button 
                    onClick={() => setCurrentStep(2)} 
                    disabled={!selectedAddressId}
                    className="w-full h-14 bg-[#F97316] hover:bg-[#EA580C] text-lg rounded-xl"
                  >
                    Continuer
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </motion.div>
              )}

              {/* ÉTAPE 2: Mode de livraison */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="bg-white rounded-2xl shadow-sm p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Mode de livraison</h2>

                    <div className="space-y-3">
                      {deliveryOptions.map((option) => (
                        <label
                          key={option.id}
                          className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            deliveryMethod === option.id 
                              ? 'border-[#F97316] bg-orange-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          } ${option.highlight ? 'ring-2 ring-[#F97316]/20' : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="delivery"
                              checked={deliveryMethod === option.id}
                              onChange={() => setDeliveryMethod(option.id)}
                              className="accent-[#F97316]"
                            />
                            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                              <option.icon size={20} className={option.highlight ? 'text-[#F97316]' : 'text-gray-600'} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{option.label}</span>
                                {option.highlight && (
                                  <span className="text-xs bg-[#F97316] text-white px-2 py-0.5 rounded-full">Recommandé</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">{option.delay}</p>
                            </div>
                            <span className={`font-semibold ${option.price === 0 ? 'text-emerald-600' : 'text-gray-900'}`}>
                              {option.price === 0 ? 'Gratuit' : formatPrice(option.price)}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>

                    {/* Résumé adresse sélectionnée */}
                    {selectedAddress && (
                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-700">Livré à :</p>
                            <p className="text-sm text-gray-600">{selectedAddress.full_name}</p>
                            <p className="text-sm text-gray-500">{selectedAddress.address_line1}, {selectedAddress.postal_code} {selectedAddress.city}</p>
                          </div>
                          <button 
                            onClick={() => setCurrentStep(1)}
                            className="text-sm text-[#F97316] hover:underline flex items-center gap-1"
                          >
                            <Edit2 size={14} />
                            Modifier
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setCurrentStep(1)} className="flex-1 h-14">
                      <ArrowLeft size={18} className="mr-2" />
                      Retour
                    </Button>
                    <Button 
                      onClick={() => setCurrentStep(3)} 
                      className="flex-1 h-14 bg-[#F97316] hover:bg-[#EA580C] text-lg"
                    >
                      Continuer
                      <ArrowRight size={20} className="ml-2" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* ÉTAPE 3: Paiement */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="bg-white rounded-2xl shadow-sm p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Paiement</h2>

                    {/* Mode préouverture */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-amber-800">Mode Préouverture</p>
                          <p className="text-xs text-amber-700 mt-1">
                            Les paiements réels ne sont pas encore activés. Clique sur "Confirmer" pour simuler une commande.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Options de paiement simulées */}
                    <div className="space-y-3">
                      <label className="block p-4 rounded-xl border-2 border-[#F97316] bg-orange-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <input type="radio" name="payment" checked readOnly className="accent-[#F97316]" />
                          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                            <CreditCard size={20} className="text-[#F97316]" />
                          </div>
                          <div className="flex-1">
                            <span className="font-semibold text-gray-900">Carte bancaire</span>
                            <p className="text-sm text-gray-500">Visa, Mastercard, CB</p>
                          </div>
                          <div className="flex gap-1">
                            <img src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@main/flags/4x3/fr.svg" className="h-4 rounded" alt="CB" />
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* Récap final */}
                    <div className="mt-6 pt-4 border-t border-gray-100 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Livraison</span>
                        <span>{selectedDelivery?.label}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Adresse</span>
                        <span>{selectedAddress?.city}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setCurrentStep(2)} className="flex-1 h-14">
                      <ArrowLeft size={18} className="mr-2" />
                      Retour
                    </Button>
                    <Button 
                      onClick={handleCreateOrder}
                      disabled={loading}
                      className="flex-1 h-14 bg-emerald-500 hover:bg-emerald-600 text-lg"
                      data-testid="btn-confirm-order"
                    >
                      {loading ? 'Traitement...' : (
                        <>
                          <Lock size={18} className="mr-2" />
                          Confirmer • {formatPrice(finalTotal)}
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-center text-gray-500 flex items-center justify-center gap-1">
                    <Shield size={12} />
                    Paiement sécurisé SSL
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Colonne récapitulatif */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-4">
              <h3 className="font-bold text-gray-900 mb-4">Ta commande</h3>

              {/* Articles */}
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-3">
                    <div className="w-14 h-14 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden">
                      {item.image ? (
                        <img src={item.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package size={20} className="text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                      <p className="text-xs text-gray-500">Qté : {item.quantity}</p>
                      <p className="text-sm font-semibold text-[#F97316]">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sous-total</span>
                  <span>{formatPrice(cartSubtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Livraison</span>
                  <span className={finalShipping === 0 ? 'text-emerald-600' : ''}>
                    {finalShipping === 0 ? 'Offerte' : formatPrice(finalShipping)}
                  </span>
                </div>
                <div className="flex justify-between font-bold pt-2 border-t border-gray-100">
                  <span>Total</span>
                  <span className="text-[#F97316]">{formatPrice(finalTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      
      <div className="h-24 lg:hidden" />
    </div>
  );
}
